import { useCallback, useMemo } from "react";
import {
  useGetList,
  useGetMany,
  useNotify,
  useList,
  ListContextProvider,
  Datagrid,
  TextField,
  DateField,
  ReferenceField,
  FunctionField,
  Loading,
} from "react-admin";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { centsToCurrency } from "../utils";

export const toCsvField = (value: any): string => {
  const str = String(value ?? "");
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const toCsv = (headers: string[], rows: Record<string, any>[]): string => {
  const headerLine = headers.map(toCsvField).join(",");
  const dataLines = rows.map((row) =>
    headers.map((h) => toCsvField(row[h])).join(",")
  );
  return [headerLine, ...dataLines].join("\n");
};

interface InstanceSalesViewProps {
  eventId: string;
  instanceId: string | null;
  eventName?: string;
  instanceLabel?: string;
}

const statusColors: Record<string, string> = {
  active: "#e8f5e9",
  pending: "#fff3e0",
  expired: "#efebe9",
  shared: "#e3f2fd",
};

const passRowSx = (record: any) => ({
  backgroundColor: statusColors[record?.status] || "inherit",
});

export const InstanceSalesView = ({
  eventId,
  instanceId,
  eventName = "event",
  instanceLabel = "",
}: InstanceSalesViewProps) => {
  const notify = useNotify();

  const { data: passes, isLoading } = useGetList("passes", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "createdate", order: "DESC" },
    filter: {
      eventId,
      eventInstanceId: instanceId,
    },
  });

  const memberIds = useMemo(
    () =>
      Array.from(
        new Set((passes || []).map((p) => p.memberId).filter(Boolean))
      ),
    [passes]
  );

  const { data: membersData } = useGetMany("members", { ids: memberIds });

  const membersMap = useMemo(() => {
    const map = new Map<string, any>();
    (membersData || []).forEach((m) => map.set(m.id, m));
    return map;
  }, [membersData]);

  const summary = useMemo(() => {
    if (!passes || passes.length === 0)
      return { total: 0, revenue: 0, byType: {} as Record<string, { count: number; revenue: number }> };

    const byType: Record<string, { count: number; revenue: number }> = {};
    let totalRevenue = 0;

    passes.forEach((pass) => {
      totalRevenue += pass.amount || 0;
      const typeName = pass.name || "Unknown";
      if (!byType[typeName]) byType[typeName] = { count: 0, revenue: 0 };
      byType[typeName].count++;
      byType[typeName].revenue += pass.amount || 0;
    });

    return { total: passes.length, revenue: totalRevenue, byType };
  }, [passes]);

  const listContext = useList({
    data: passes || [],
    isLoading,
    sort: { field: "createdate", order: "DESC" },
  });

  const handleExport = useCallback(async () => {
    if (!passes || passes.length === 0) {
      notify("No attendees to export", { type: "warning" });
      return;
    }

    const ticketCounts: Record<string, { count: number; status: string }> = {};
    passes.forEach((pass) => {
      const mid = pass.memberId;
      if (!ticketCounts[mid]) {
        ticketCounts[mid] = { count: 0, status: pass.status || "" };
      }
      ticketCounts[mid].count++;
    });

    const records = Object.entries(ticketCounts)
      .map(([memberId, info]) => {
        const member = membersMap.get(memberId);
        const displayName = member?.displayName || "";
        const nameParts = displayName.split(" ");
        return {
          "First Name": nameParts[0] || "",
          "Last Name": nameParts.slice(1).join(" ") || "",
          Email: member?.email || "",
          Phone: member?.phoneNumber || "",
          Tickets: info.count,
        };
      })
      .sort((a, b) => a["Last Name"].localeCompare(b["Last Name"]));

    try {
      const headers = ["First Name", "Last Name", "Email", "Phone", "Tickets"];
      const csv = toCsv(headers, records);

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = eventName.replace(/[^a-zA-Z0-9-]/g, "_");
      const safeDate = instanceLabel.replace(/[^a-zA-Z0-9-]/g, "_");
      link.download = `rsvp_${safeName}_${safeDate}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      notify("RSVP list exported successfully");
    } catch {
      notify("Error exporting RSVP list", { type: "error" });
    }
  }, [passes, membersMap, eventName, instanceLabel, notify]);

  if (!instanceId) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        Select an event instance above to view sales and attendees.
      </Typography>
    );
  }

  if (isLoading) return <Loading />;

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Tickets Sold
              </Typography>
              <Typography variant="h4">{summary.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Total Revenue
              </Typography>
              <Typography variant="h4">
                {centsToCurrency(summary.revenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                By Ticket Type
              </Typography>
              {Object.entries(summary.byType).map(([type, data]) => (
                <Typography key={type} variant="body2">
                  {type}: {data.count} ({centsToCurrency(data.revenue)})
                </Typography>
              ))}
              {Object.keys(summary.byType).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No sales yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Export Button */}
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          onClick={handleExport}
          variant="outlined"
          startIcon={<DownloadIcon />}
          disabled={!passes || passes.length === 0}
          size="small"
        >
          Export RSVPs
        </Button>
      </Box>

      {/* Attendee Table */}
      {passes && passes.length > 0 ? (
        <ListContextProvider value={listContext}>
          <Datagrid bulkActionButtons={false} rowSx={passRowSx}>
            <DateField source="createdate" label="Purchase Date" />
            <TextField source="name" label="Ticket Type" />
            <ReferenceField
              source="memberId"
              reference="members"
              label="Attendee"
              link={false}
            >
              <TextField source="displayName" />
            </ReferenceField>
            <ReferenceField
              source="memberId"
              reference="members"
              label="Email"
              link={false}
            >
              <TextField source="email" />
            </ReferenceField>
            <FunctionField
              label="Amount"
              render={(record: any) => centsToCurrency(record?.amount || 0)}
            />
            <TextField source="status" />
          </Datagrid>
        </ListContextProvider>
      ) : (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No tickets sold for this instance yet.
        </Typography>
      )}
    </Box>
  );
};
